import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { SEO } from '../components/common/SEO';
import { useCanonicalUrl } from '../hooks/useCanonicalUrl';
import { UsernameInput } from '../components/UsernameInput';
import { PasskeysManager } from '../components/PasskeysManager';
import { 
  User, 
  Bell, 
  Shield, 
  Users, 
  Camera, 
  Save, 
  Edit3, 
  Trash2, 
  Download,
  Plus,
  X,
  Key,
  Settings
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface UserProfile {
  id: string;
  email: string;
  display_name?: string;
  username?: string;
  phone_number?: string;
  avatar_url?: string;
  whatsapp_opt_in?: boolean;
  notification_preferences?: {
    email_notifications: boolean;
    push_notifications: boolean;
    wishlist_updates: boolean;
    price_drops: boolean;
    birthday_reminders: boolean;
  };
  created_at: string;
}

interface Group {
  id: string;
  name: string;
  description?: string;
  members: GroupMember[];
  created_at: string;
}

interface GroupMember {
  id: string;
  user_id: string;
  group_id: string;
  role: 'admin' | 'member';
  display_name?: string;
  email?: string;
  avatar_url?: string;
}

export const AccountSettingsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'groups' | 'notifications' | 'privacy' | 'passkeys' | 'account'>('profile');
  const canonicalUrl = useCanonicalUrl();
  
  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    display_name: '',
    username: '',
    phone_number: '',
    whatsapp_opt_in: false
  });
  
  // Groups state
  const [groups, setGroups] = useState<Group[]>([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupForm, setNewGroupForm] = useState({
    name: '',
    description: ''
  });
  
  // Notifications state
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_notifications: true,
    push_notifications: true,
    wishlist_updates: true,
    price_drops: true,
    birthday_reminders: true
  });
  
  // Privacy state
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: 'public',
    wishlist_visibility: 'friends',
    show_email: false,
    show_phone: false
  });

  const loadUserData = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Load user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      if (profileData) {
        setProfile(profileData);
        setProfileForm({
          display_name: profileData.display_name || '',
          username: profileData.username || '',
          phone_number: profileData.phone_number || '',
          whatsapp_opt_in: profileData.whatsapp_opt_in || false
        });
        setNotificationPrefs(profileData.notification_preferences || {
          email_notifications: true,
          push_notifications: true,
          wishlist_updates: true,
          price_drops: true,
          birthday_reminders: true
        });
      } else {
        // Create default profile if none exists
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            display_name: user.user_metadata?.full_name || '',
            notification_preferences: {
              email_notifications: true,
              push_notifications: true,
              wishlist_updates: true,
              price_drops: true,
              birthday_reminders: true
            }
          })
          .select()
          .single();

        if (createError) throw createError;
        
        setProfile(newProfile);
        setProfileForm({
          display_name: newProfile.display_name || '',
          username: newProfile.username || '',
          phone_number: newProfile.phone_number || '',
          whatsapp_opt_in: newProfile.whatsapp_opt_in || false
        });
      }

      // Load user groups - handle gracefully if schema isn't ready yet
      try {
        // Load groups where user is admin
        const { data: adminGroups, error: adminGroupsError } = await supabase
          .from('groups')
          .select(`
            *,
            members:group_members(
              *,
              profile:profiles(display_name, email, avatar_url)
            )
          `)
          .eq('admin_id', user.id);

        if (adminGroupsError) throw adminGroupsError;

        // Then get group memberships for this user
        const { data: memberships, error: membershipsError } = await supabase
          .from('group_members')
          .select(`
            group_id,
            groups!inner(
              *,
              members:group_members(
                *,
                profile:profiles(display_name, email, avatar_url)
              )
            )
          `)
          .eq('user_id', user.id);

        if (membershipsError) throw membershipsError;

        // Extract groups from memberships
        const memberGroups = memberships?.map(membership => membership.groups).filter(Boolean) || [];

        // Combine both results and remove duplicates
        const allGroups = [...(adminGroups || []), ...memberGroups];
        const uniqueGroups = allGroups.filter((group, index, self) => 
          index === self.findIndex(g => g.id === group.id)
        );
        setGroups(uniqueGroups);
      } catch (groupsError) {
        console.warn('Groups schema not ready yet, skipping groups loading:', groupsError);
        // Set empty groups array if schema isn't ready
        setGroups([]);
      }

    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user, loadUserData]);

  const handleSaveProfile = async () => {
    if (!user || !profile) return;
    
    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: profileForm.display_name,
          username: profileForm.username,
          phone_number: profileForm.phone_number,
          whatsapp_opt_in: profileForm.whatsapp_opt_in,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setProfile(prev => prev ? {
        ...prev,
        display_name: profileForm.display_name,
        username: profileForm.username,
        phone_number: profileForm.phone_number,
        whatsapp_opt_in: profileForm.whatsapp_opt_in
      } : null);

      setEditingProfile(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          notification_preferences: notificationPrefs,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Notification preferences saved!');
    } catch (error) {
      console.error('Error updating notifications:', error);
      toast.error('Failed to update notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      
      const { data: newGroup, error } = await supabase
        .from('groups')
        .insert({
          name: newGroupForm.name,
          description: newGroupForm.description,
          admin_id: user.id
        })
        .select()
        .single();

      if (error) throw error;

      // Add creator as admin member
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: newGroup.id,
          user_id: user.id,
          role: 'admin'
        });

      if (memberError) throw memberError;

      setGroups(prev => [...prev, { ...newGroup, members: [] }]);
      setNewGroupForm({ name: '', description: '' });
      setShowCreateGroup(false);
      toast.success('Group created successfully!');
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  const handleLeaveGroup = async (groupId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', user.id);

      if (error) throw error;

      setGroups(prev => prev.filter(group => group.id !== groupId));
      toast.success('Left group successfully');
    } catch (error) {
      console.error('Error leaving group:', error);
      toast.error('Failed to leave group');
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to delete all your account data? This will permanently remove all your wishlists, profile information, and group memberships. This action cannot be undone.'
    );
    
    if (!confirmed) return;
    
    try {
      setSaving(true);
      
      // Delete all user's wishlists and items first
      const { error: wishlistsError } = await supabase
        .from('wishlists')
        .delete()
        .eq('user_id', user.id);

      if (wishlistsError) {
        console.warn('Error deleting wishlists:', wishlistsError);
        // Continue anyway as this might be handled by cascade
      }

      // Delete user profile
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);

      if (profileError) {
        console.warn('Error deleting profile:', profileError);
        // Continue anyway
      }

      // Delete group memberships
      const { error: groupMembersError } = await supabase
        .from('group_members')
        .delete()
        .eq('user_id', user.id);

      if (groupMembersError) {
        console.warn('Error deleting group memberships:', groupMembersError);
      }

      // Sign out user
      await supabase.auth.signOut();
      
      toast.success('Account data deleted successfully. You have been signed out.');
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      
      // If we can't delete the account, at least sign them out
      try {
        await supabase.auth.signOut();
        toast.error('Account deletion partially completed. You have been signed out. Please contact support to complete the process.');
        window.location.href = '/';
      } catch (signOutError) {
        toast.error('Failed to delete account. Please contact support.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      
      // Export user data
      const { data: wishlists } = await supabase
        .from('wishlists')
        .select(`
          *,
          items:wishlist_items(*)
        `)
        .eq('user_id', user.id);

      const exportData = {
        profile,
        wishlists: wishlists || [],
        groups,
        export_date: new Date().toISOString()
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wishful-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Please sign in</h1>
          <p className="text-gray-600 mb-6">You need to be signed in to access account settings.</p>
          <Button onClick={() => window.location.href = '/auth'}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'groups', label: 'Groups', icon: Users },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'passkeys', label: 'Passkeys', icon: Key },
    { id: 'account', label: 'Account', icon: Settings }
  ];

  return (
    <>
      <SEO 
        title="Account Settings - Wishful"
        description="Manage your account settings, profile, groups, and preferences"
        url={canonicalUrl}
      />
      
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
            <p className="mt-2 text-gray-600">Manage your account, profile, and preferences</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <div className="lg:w-64">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        activeTab === tab.id
                          ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-500'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
                    {!editingProfile && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingProfile(true)}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit Profile
                      </Button>
                    )}
                  </div>

                  <div className="space-y-6">
                    {/* Avatar */}
                    <div className="flex items-center space-x-4">
                      <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center">
                        {profile?.avatar_url ? (
                          <img
                            src={profile.avatar_url}
                            alt="Profile"
                            className="h-20 w-20 rounded-full object-cover"
                          />
                        ) : (
                          <User className="h-8 w-8 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {profile?.display_name || 'No name set'}
                        </h3>
                        <p className="text-gray-600">{profile?.email}</p>
                        {editingProfile && (
                          <Button variant="outline" size="sm" className="mt-2">
                            <Camera className="h-4 w-4 mr-2" />
                            Change Avatar
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Profile Form */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Display Name
                        </label>
                        <Input
                          value={profileForm.display_name}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, display_name: e.target.value }))}
                          disabled={!editingProfile}
                          placeholder="Enter your display name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Username
                        </label>
                        {profile?.username ? (
                          <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 select-text">
                            @{profile.username}
                          </div>
                        ) : (
                          <UsernameInput
                            value={profileForm.username}
                            onChange={(value) => setProfileForm(prev => ({ ...prev, username: value }))}
                            userId={user?.id}
                            disabled={!editingProfile}
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <Input
                          value={profileForm.phone_number}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, phone_number: e.target.value }))}
                          disabled={!editingProfile}
                          placeholder="Enter your phone number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <Input
                          value={profile?.email || ''}
                          disabled
                          className="bg-gray-50"
                        />
                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                      </div>
                    </div>

                    {/* WhatsApp Opt-in */}
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="whatsapp_opt_in"
                        checked={profileForm.whatsapp_opt_in}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, whatsapp_opt_in: e.target.checked }))}
                        disabled={!editingProfile}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="whatsapp_opt_in" className="text-sm text-gray-700">
                        Allow WhatsApp notifications
                      </label>
                    </div>

                    {/* Action Buttons */}
                    {editingProfile && (
                      <div className="flex space-x-3">
                        <Button
                          onClick={handleSaveProfile}
                          loading={saving}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingProfile(false);
                            setProfileForm({
                              display_name: profile?.display_name || '',
                              username: profile?.username || '',
                              phone_number: profile?.phone_number || '',
                              whatsapp_opt_in: profile?.whatsapp_opt_in || false
                            });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Groups Tab */}
              {activeTab === 'groups' && (
                <div className="space-y-6">
                  <Card className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">My Groups</h2>
                      <Button
                        onClick={() => setShowCreateGroup(true)}
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Group
                      </Button>
                    </div>

                    {groups.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No groups yet</h3>
                        <p className="text-gray-600 mb-4">Create a group to share wishlists with family and friends</p>
                        <Button onClick={() => setShowCreateGroup(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Your First Group
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {groups.map((group) => (
                          <div key={group.id} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-medium text-gray-900">{group.name}</h3>
                                {group.description && (
                                  <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-2">
                                  {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleLeaveGroup(group.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="h-4 w-4 mr-2" />
                                Leave
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>

                  {/* Create Group Modal */}
                  {showCreateGroup && (
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Group</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Group Name
                          </label>
                          <Input
                            value={newGroupForm.name}
                            onChange={(e) => setNewGroupForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter group name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description (Optional)
                          </label>
                          <textarea
                            value={newGroupForm.description}
                            onChange={(e) => setNewGroupForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Enter group description"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            rows={3}
                          />
                        </div>
                        <div className="flex space-x-3">
                          <Button
                            onClick={handleCreateGroup}
                            loading={saving}
                            disabled={!newGroupForm.name.trim()}
                          >
                            Create Group
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowCreateGroup(false);
                              setNewGroupForm({ name: '', description: '' });
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Notification Preferences</h2>
                    <Button
                      onClick={handleSaveNotifications}
                      loading={saving}
                      size="sm"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Preferences
                    </Button>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-4">
                      {Object.entries(notificationPrefs).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {key === 'email_notifications' && 'Receive notifications via email'}
                              {key === 'push_notifications' && 'Receive push notifications'}
                              {key === 'wishlist_updates' && 'Get notified when wishlists are updated'}
                              {key === 'price_drops' && 'Get notified when prices drop'}
                              {key === 'birthday_reminders' && 'Get birthday reminders for group members'}
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => setNotificationPrefs(prev => ({
                                ...prev,
                                [key]: e.target.checked
                              }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Privacy Tab */}
              {activeTab === 'privacy' && (
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Privacy Settings</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Visibility
                      </label>
                      <select
                        value={privacySettings.profile_visibility}
                        onChange={(e) => setPrivacySettings(prev => ({ ...prev, profile_visibility: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="public">Public</option>
                        <option value="friends">Friends Only</option>
                        <option value="private">Private</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Wishlist Visibility
                      </label>
                      <select
                        value={privacySettings.wishlist_visibility}
                        onChange={(e) => setPrivacySettings(prev => ({ ...prev, wishlist_visibility: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="public">Public</option>
                        <option value="friends">Friends Only</option>
                        <option value="private">Private</option>
                      </select>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">Show Email Address</h3>
                          <p className="text-sm text-gray-600">Allow others to see your email address</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={privacySettings.show_email}
                            onChange={(e) => setPrivacySettings(prev => ({ ...prev, show_email: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">Show Phone Number</h3>
                          <p className="text-sm text-gray-600">Allow others to see your phone number</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={privacySettings.show_phone}
                            onChange={(e) => setPrivacySettings(prev => ({ ...prev, show_phone: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Passkeys Tab */}
              {activeTab === 'passkeys' && user && (
                <Card className="p-6">
                  <PasskeysManager userId={user.id} />
                </Card>
              )}

              {/* Account Tab */}
              {activeTab === 'account' && (
                <div className="space-y-6">
                  <Card className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Actions</h2>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">Export Data</h3>
                          <p className="text-sm text-gray-600">Download all your data in JSON format</p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={handleExportData}
                          loading={saving}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Export Data
                        </Button>
                      </div>

                      <div className="flex items-center justify-between p-4 border rounded-lg border-red-200 bg-red-50">
                        <div>
                          <h3 className="font-medium text-red-900">Delete Account Data</h3>
                          <p className="text-sm text-red-600">Permanently delete all your wishlist data and profile information</p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={handleDeleteAccount}
                          loading={saving}
                          className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Data
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
